import React, { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format, subYears } from 'date-fns';
import {
	AlertCircle,
	ImageIcon,
	Loader2,
	Pen,
	Plus,
	Trash,
	X
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useFormContext, useFormState } from 'react-hook-form';

import DateControl from '@/components/form/date-control';
import InputControl from '@/components/form/input-control';
import SelectControl from '@/components/form/select-control';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import PreviewFileDialog from '@/components/ui/preview-file';
import { WithStateQuery } from '@/components/ui/with-state';
import { useConfirmation } from '@/hooks/use-confirm-dialog';
import { useResponse } from '@/hooks/use-dialog';
import { cn } from '@/lib/utils';
import HookFormProvider from '@/providers/hook-form-providers';
import { fileMutations, fileQueries } from '@/queries/file/file.queries';
import { masterQueries } from '@/queries/master/master.queries';
import {
	vendorMutations,
	vendorQueries
} from '@/queries/vendor/vendor.queries';
import { noOp } from '@/services/fn';
import { extractFileParts, onFileRejected } from '@/services/string';
import { useDialogStore } from '@/store';
import {
	picDataSchema,
	type PicDataSchema
} from '@/types/form/account-manager/form';
import type { DataTableResponse } from '@/types/response';
import type { VendorPicDataResponse } from '@/types/vendor/vendor';

const MAX_PIC_DATA = 3;

function Delete({ id }: Readonly<{ id: number }>) {
	const setDialog = useDialogStore((s) => s.setStore);
	const setAction = useDialogStore((s) => s.setAction);
	const onOpen = useDialogStore((s) => s.onOpenChange);

	const action = useMutation(vendorMutations.picDataDelete(id));

	return (
		<Button
			variant={'outline'}
			size={'icon'}
			className="border-destructive text-destructive hover:text-destructive/50 hover:border-destructive/70 w-20 border-2 text-sm"
			onClick={() => {
				setDialog('variant', 'warning');
				setDialog('confirmText', 'Ya, Hapus');
				setDialog('title', 'Hapus Data?');
				setDialog(
					'description',
					'Apakah Anda yakin ingin menghapus data penanggung jawab?'
				);
				setDialog('cancelText', 'Batal');
				setAction('onCancel', () => onOpen(false));
				setAction('onConfirm', () =>
					action.mutate(undefined, {
						onSuccess: () => {
							setDialog('variant', 'success');
							setDialog('confirmText', 'OK');
							setDialog('title', 'Berhasil');
							setDialog('description', 'Data Penanggung Jawab Berhasil Dihapus');
							setAction('onConfirm', () => onOpen(false));
							onOpen(true);
						},
						onError: ({ message }) => {
							setDialog('variant', 'error');
							setDialog('title', 'Gagal');
							setDialog('description', message);
							setDialog('confirmText', 'OK');
							onOpen(true);
							setAction('onConfirm', () => onOpen(false));
						}
					})
				);
				onOpen(true);
			}}
		>
			<Trash />
			Hapus
		</Button>
	);
}

const columns = ({
	handleUpdate,
	isDisabled
}: {
	handleUpdate: (data: PicDataSchema, id: number) => void,
	isDisabled: boolean
}): ColumnDef<VendorPicDataResponse>[] => [
	{
		accessorFn: (_, i) => i.toString(),
		accessorKey: 'no',
		header: 'No',
		cell: ({ row, table: { getState } }) =>
			row.index +
			1 +
			getState().pagination.pageIndex * getState().pagination.pageSize,
		meta: { className: 'w-4 text-center', id: 'no' }
	},
	{
		accessorFn: (r) => r.name,
		accessorKey: 'name',
		header: 'Nama Lengkap',
		cell: ({ row: { original } }) => original.name,
		meta: { id: 'name' }
	},
	{
		accessorFn: (r) => r.email,
		accessorKey: 'email',
		header: 'Email',
		cell: ({ row: { original } }) => original.email,
		meta: { id: 'email' }
	},
	{
		accessorFn: (r) => r.positionName,
		accessorKey: 'positionName',
		header: 'Posisi',
		cell: ({ row: { original } }) => original.positionName,
		meta: { id: 'positionName' }
	},
	{
		accessorFn: (r) => r.occupationName,
		accessorKey: 'occupationName',
		header: 'Jabatan',
		cell: ({ row: { original } }) => original.occupationName,
		meta: { id: 'occupationName' }
	},
	{
		accessorFn: (r) => r.phoneNumber,
		accessorKey: 'phoneNumber',
		header: 'No Handphone',
		cell: ({ row: { original } }) => original.phoneNumber,
		meta: { id: 'phoneNumber' }
	},
	{
		accessorFn: (r) => r.birthDate,
		accessorKey: 'birthDate',
		header: 'Tanggal Lahir',
		cell: ({ row: { original } }) => format(original.birthDate, 'dd MMMM yyyy'),
		meta: { id: 'birthDate' }
	},
	{
		accessorFn: (r) => r.nik,
		accessorKey: 'nik',
		header: 'NIK',
		cell: ({ row: { original } }) => original.nik,
		meta: { id: 'nik' }
	},
	{
		accessorFn: (r) => r.ktpFile,
		accessorKey: 'ktpFile',
		header: 'Foto KTP',
		cell: ({ row: { original } }) => (
			<PreviewFileDialog fileKey={original.ktpFile} label="Lihat Detail" />
		),
		meta: { id: 'ktpFile' }
	},
	{
		accessorFn: (r) => r.selfieFile,
		accessorKey: 'selfieFile',
		header: 'Foto Selfie',
		cell: ({ row: { original } }) => (
			<PreviewFileDialog fileKey={original.selfieFile} label="Lihat Detail" />
		),
		meta: { id: 'selfieFile' }
	},
	{
		accessorFn: (r) => r.statusId,
		accessorKey: 'statusId',
		header: 'Status E-Sign',
		cell: ({ row: { original } }) => <Badge>{original.statusName}</Badge>,
		meta: { id: 'statusId' }
	},
	{
		accessorFn: () => 'actions',
		accessorKey: 'actions',
		header: 'Aksi',
		cell: ({ row: { original } }) => (
			<div className="flex gap-2 py-2">
				<Button
					variant={'outline'}
					size={'icon'}
					className="w-20 border-2 text-sm"
					onClick={() =>
						handleUpdate(
							{
								name: original.name,
								birthDate: original.birthDate,
								email: original.email,
								ktpFile: original.ktpFile,
								nik: original.nik,
								occupationId: original.occupationId,
								phoneNumber: original.phoneNumber,
								positionId: original.positionId,
								selfieFile: original.selfieFile
							},
							original.id
						)
					}
				>
					<Pen />
					Ubah
				</Button>
				<Delete id={original.id} />
			</div>
		),
		meta: {
			id: 'actions',
			isVisible: !isDisabled
		}
	}
];

function Occupation() {
	const options = useQuery(masterQueries.listOccupation({ showAll: true }));

	return (
		<SelectControl
			name="occupationId"
			options={
				options.data
					? options.data.data.map((v) => ({
							...v,
							label: v.name,
							value: v.id
						}))
					: []
			}
			label="Jabatan"
			isLoading={options.isPending}
			isSearchable
			className="md:col-span-2"
		/>
	);
}

function Image({ fileKey }: Readonly<{ fileKey: string }>) {
	return (
		<img
			src={fileKey}
			alt="NPWP Preview"
			className="max-h-full max-w-full rounded-md object-contain shadow-sm"
		/>
	);
}

function PreviewFile({
	fileKey,
	isDirectUrl = false
}: Readonly<{ fileKey: string, isDirectUrl?: boolean }>) {
	const file = useQuery({
		...fileQueries.getPresign({ key: fileKey }),
		enabled: !isDirectUrl
	});

	if (isDirectUrl) {
		return <Image fileKey={fileKey} />;
	}
	return (
		<WithStateQuery state={file}>
			{({ data: { url } }) => <Image fileKey={url} />}
		</WithStateQuery>
	);
}
function FileUpload({
	name,
	isDisabled,
	vendorName,
	image
}: Readonly<{
	name: 'ktpFile' | 'selfieFile',
	isDisabled?: boolean,
	vendorName: string,
	image: string | null
}>) {
	const { setValue, getValues } = useFormContext<PicDataSchema>();
	const { errors } = useFormState<PicDataSchema>({ name });
	const { isDirty } = useFormState<PicDataSchema>({ name });

	const [file, setFile] = useState(getValues(name));
	const [visible, setVisible] = useState<boolean>(false);
	const [progress, setProgress] = useState<number>(0);
	const [previewUrl, setPreviewUrl] = useState<{
		key: string | null,
		isDirectUrl: boolean
	}>({ key: file, isDirectUrl: false });

	const onOpen = useDialogStore((s) => s.onOpenChange);
	const { askCloseIfDirty } = useConfirmation();
	const { onError } = useResponse();

	const uploadMutation = useMutation(fileMutations.uploadPresign);

	const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
		accept: {
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		},
		disabled: uploadMutation.isPending || isDisabled,
		noKeyboard: true,
		onDropAccepted: (acceptedFiles) => {
			const file = acceptedFiles[0];
			if (!file) return;

			setPreviewUrl({ key: null, isDirectUrl: false });
			setProgress(0);

			const date = new Date();
			const { fileName, fileExt } = extractFileParts(file.name);
			const fileKey = `${vendorName}/${date.getFullYear()}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${crypto.randomUUID()}/${fileName.slice(0, 50)}.${fileExt}`;

			uploadMutation.mutate(
				{
					key: fileKey,
					file: file,
					onProgress: (val) => setProgress(val)
				},
				{
					onSuccess: (sasUrl) => {
						setPreviewUrl({ key: sasUrl, isDirectUrl: true });
						setFile(fileKey);
					},
					onError: noOp
				}
			);
		},
		noClick: true,
		multiple: false,
		maxSize: 5_242_880,
		onDropRejected: (err) => {
			const errFile = err[0];
			if (!errFile) return;

			onError({
				message: onFileRejected({ code: errFile.errors[0]?.code, type: 'image' }),
				confirmText: 'Coba Lagi'
			});
		}
	});

	const handleReset = ({
		reset,
		isCreated
	}: {
		reset?: boolean,
		isCreated?: boolean
	}) => {
		const file = isCreated ? getValues(name) : image;
		setFile(reset ? null : file);
		setPreviewUrl({ key: reset ? null : file, isDirectUrl: false });
		setProgress(0);
		uploadMutation.reset();
	};

	const handleSave = () => {
		setValue(name, file, { shouldDirty: true });
		setVisible(false);
	};

	return (
		<>
			<div className="flex w-full flex-col gap-2 md:col-span-2 lg:col-span-3">
				<Label className="text-label-input text-xs">
					{name === 'ktpFile' ? 'Foto KTP' : 'Foto Selfie'}
				</Label>
				<Button
					variant={'outline'}
					className="flex h-12 items-start justify-start"
					onClick={() => setVisible(true)}
					type="button"
				>
					<img src="/icon/upload.webp" alt="icon-upload" className="size-4" />
					<span className="flex flex-col items-start">
						<p className="text-xs text-black">
							{previewUrl.key ? 'Lihat Dokumen (1)' : 'Unggah Dokumen'}
						</p>
						<p className="text-secondary text-xs font-light">
							{previewUrl.key
								? 'Lihat atau unggah ulang dokumen'
								: 'Dokumen: JPG,JPEG, PNG (maks. 5MB)'}
						</p>
					</span>
				</Button>
				{errors[name]?.message ? (
					<p
						data-slot="form-message"
						className={cn('text-destructive -mt-2 text-sm')}
					>
						{errors[name]?.message}
					</p>
				) : null}
			</div>
			<Dialog
				open={visible}
				onOpenChange={(v) => {
					if (!v) {
						askCloseIfDirty(
							isDirty || file !== image,
							() => {},
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						);
					}
				}}
			>
				<DialogContent
					onPointerDownOutside={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
					onEscapeKeyDown={(e) =>
						askCloseIfDirty(
							isDirty || file !== image,
							() => e.preventDefault(),
							() => {
								handleReset({ isCreated: image === null });
								setVisible(false);
								onOpen(false);
							}
						)
					}
				>
					<DialogTitle className="flex flex-col">
						<span className="text-lg text-black">
							{name === 'ktpFile' ? 'Foto KTP' : 'Foto Selfie'}
						</span>
						<span className="text-label-input">
							Silakan unggah {name === 'ktpFile' ? 'Foto KTP' : 'Foto Selfie'} Anda
						</span>
					</DialogTitle>
					<div
						{...getRootProps({
							className: cn(
								'dropzone border-2 border-slate-300 border-dashed h-36 flex flex-col gap-1 justify-center items-center p-8 text-center rounded-lg transition delay-100 duration-300 ease-in-out',
								isDragAccept && 'bg-slate-400/50'
							)
						})}
					>
						<input {...getInputProps()} />

						{uploadMutation.isPending ? (
							<div className="z-10 flex flex-col items-center gap-2">
								<Loader2 className="size-8 animate-spin text-blue-500" />
								<span className="text-sm font-medium">Uploading... {progress}%</span>
							</div>
						) : null}

						{!uploadMutation.isPending && previewUrl.key ? (
							<div className="relative flex h-full w-full items-center justify-center p-2">
								<PreviewFile
									fileKey={previewUrl.key}
									isDirectUrl={previewUrl.isDirectUrl}
								/>
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleReset({ reset: true, isCreated: image === null });
									}}
									className="absolute -top-2 -right-2 rounded-full border bg-white p-1 shadow-md transition hover:bg-gray-100"
								>
									<X className="size-4 text-gray-500" />
								</button>
							</div>
						) : (
							/* 3. IDLE STATE */
							!uploadMutation.isPending && (
								<>
									<div
										className="flex w-full cursor-pointer items-center justify-center rounded bg-neutral-50 p-4 transition hover:bg-neutral-100"
										onClick={open}
										role="none"
									>
										<ImageIcon className="size-6 text-gray-400" />
									</div>
									<span className="mt-4 font-medium text-black">
										Unggah {name === 'ktpFile' ? 'Foto KTP' : 'Foto Selfie'}
									</span>
									<span className="text-secondary mt-1 text-xs">
										Unggah file JPG, JPEG, PNG (maks. 5MB)
									</span>
								</>
							)
						)}
					</div>
					<DialogFooter className="shrink-0 sm:justify-end">
						<Button
							type="button"
							disabled={uploadMutation.isPending}
							onClick={handleSave}
						>
							Simpan
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function FormPicData({
	birthDate = null,
	email = null,
	ktpFile = null,
	name = null,
	nik = null,
	occupationId = null,
	phoneNumber = null,
	positionId = null,
	selfieFile = null,
	id,
	onClose,
	isDisabled = false,
	vendorName
}: Readonly<
	PicDataSchema & {
		id?: number,
		onClose: () => void,
		isDisabled?: boolean,
		vendorName: string
	}
>) {
	const { onError, onSuccess } = useResponse();
	const create = useMutation(vendorMutations.picData);
	const update = useMutation(vendorMutations.picDataUpdate(id));

	const handleSubmit = (data: PicDataSchema) => {
		if (id) {
			update.mutate(
				{
					...data,
					birthDate: data.birthDate ? format(data.birthDate, 'yyyy-MM-dd') : null
				},
				{
					onSuccess: () => {
						onSuccess({ message: 'Data Penanggung Jawab Berhasil Diubah' });
						onClose();
					},
					onError: ({ message }) => onError({ message })
				}
			);

			return;
		}

		create.mutate(
			{
				...data,
				birthDate: data.birthDate ? format(data.birthDate, 'yyyy-MM-dd') : null
			},
			{
				onSuccess: () => {
					onSuccess({ message: 'Data Penanggung Jawab Berhasil Ditambahkan' });
					onClose();
				},
				onError: ({ message }) => onError({ message })
			}
		);
	};

	return (
		<HookFormProvider
			schema={picDataSchema}
			defaultValues={{
				birthDate,
				email,
				ktpFile,
				name,
				nik,
				occupationId,
				phoneNumber,
				positionId,
				selfieFile,
				position: 'Vendor Authorize Officer'
			}}
			onSubmit={handleSubmit}
			className="my-2"
			formOptions={{ disabled: isDisabled, mode: 'onTouched' }}
		>
			<div className="grid w-full grid-cols-1 gap-x-4 gap-y-2 md:grid-cols-4 lg:grid-cols-6">
				<InputControl
					name="name"
					label="Nama Lengkap"
					className="md:col-span-2"
					isDisabled={isDisabled}
				/>
				<InputControl
					name="email"
					label="Email"
					className="md:col-span-2"
					isDisabled={isDisabled}
				/>
				<Occupation />
				<InputControl
					name="nik"
					label="NIK"
					className="md:col-span-2"
					isDisabled={isDisabled}
				/>
				<InputControl
					name="phoneNumber"
					label="No Handphone"
					className="md:col-span-2"
					isDisabled={isDisabled}
				/>
				<DateControl
					name="birthDate"
					placeholder="Masukkan Tanggal Lahir"
					label="Tanggal Lahir"
					className="md:col-span-2"
					required
					isDisabled={isDisabled}
					calendarProps={{
						captionLayout: 'dropdown',
						endMonth: subYears(new Date(), 18)
					}}
				/>

				<div className="grid grid-cols-3 md:col-span-4 lg:col-span-6">
					<InputControl
						name="position"
						value={'Vendor Authorize Officer'}
						isDisabled
						label="Posisi"
					/>
				</div>

				<FileUpload
					name="ktpFile"
					isDisabled={isDisabled}
					vendorName={vendorName}
					image={ktpFile}
				/>
				<FileUpload
					name="selfieFile"
					isDisabled={isDisabled}
					vendorName={vendorName}
					image={selfieFile}
				/>
			</div>
			{isDisabled ? null : (
				<div className="mt-2 flex w-full justify-end">
					<Button>Simpan</Button>
				</div>
			)}
		</HookFormProvider>
	);
}

export default function FormPic({
	isDisabled = false,
	vendorName,
	pics
}: Readonly<{
	isDisabled?: boolean,
	vendorName: string,
	pics?: DataTableResponse<VendorPicDataResponse>
}>) {
	const [visible, setVisible] = useState<{
		open: boolean,
		data: PicDataSchema,
		id?: number
	}>({
		open: false,
		data: {
			birthDate: null,
			email: null,
			ktpFile: null,
			name: null,
			nik: null,
			occupationId: null,
			phoneNumber: null,
			positionId: 1,
			selfieFile: null
		}
	});

	const picData = useQuery({
		...vendorQueries.picData(),
		enabled: !pics
	});

	const handleOpenDialog = (data: PicDataSchema, id?: number) => {
		setVisible({ open: true, id, data });
	};

	const handleCloseDialog = () =>
		setVisible((prev) => ({ ...prev, open: false }));

	return (
		<>
			<div className="my-2 flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<p className="col-span-full text-xl font-medium">
						Informasi Penanggung Jawab
					</p>
					<WithStateQuery state={picData}>
						{({ count }) =>
							count < MAX_PIC_DATA && !isDisabled ? (
								<Button
									type="button"
									onClick={() =>
										handleOpenDialog({
											birthDate: null,
											email: null,
											ktpFile: null,
											name: null,
											nik: null,
											occupationId: null,
											phoneNumber: null,
											positionId: 1,
											selfieFile: null
										})
									}
								>
									<Plus />
									Tambah Data
								</Button>
							) : null
						}
					</WithStateQuery>
				</div>

				<DataTable
					columns={columns({ handleUpdate: handleOpenDialog, isDisabled })}
					data={pics?.data ?? picData.data?.data}
					isLoading={pics ? false : picData.isPending}
					total={pics ? pics.count : picData.data?.count}
					isUsePagination={false}
				/>

				<div className="flex items-center text-orange-500">
					<AlertCircle className="mr-2 h-4 w-4" />
					<span className="text-xs font-medium">
						*Penanggung Jawab yang akan tandatangan Dokumen NDA
					</span>
				</div>
			</div>
			<Dialog
				open={visible.open}
				onOpenChange={() =>
					setVisible((prev) => ({
						...prev,
						open: false
					}))
				}
			>
				<DialogContent className="min-w-2/3">
					<DialogTitle className="flex flex-col">
						<span className="text-lg text-black">Informasi Penanggung Jawab</span>
						<span className="text-label-input">
							Masukkan Informasi Penanggung Jawab Anda
						</span>
					</DialogTitle>
					<FormPicData
						{...visible.data}
						id={visible.id}
						onClose={handleCloseDialog}
						isDisabled={isDisabled}
						vendorName={vendorName}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
